import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, PackageIcon, TagIcon, DollarSignIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
// Shared mock — would normally come from a store / API
const mockProducts: Record<string, {
  id: string;
  name: string;
  description: string;
  price: number;
  salePrice?: number;
  sku: string;
  stockQuantity: number;
  category: string;
  imageUrl: string;
  isActive: boolean;
  options: {
    name: string;
    values: string[];
  }[];
  modifiers: {
    name: string;
    price: number;
  }[];
}> = {
  prod1: {
    id: 'prod1',
    name: 'Beach Towel',
    description: 'Soft cotton beach towel — 80x160 cm',
    price: 29.99,
    sku: 'BT-001',
    stockQuantity: 45,
    category: 'Accessories',
    imageUrl: 'https://images.unsplash.com/photo-1601060869389-3a4ee2f44a7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    isActive: true,
    options: [{
      name: 'Color',
      values: ['Blue', 'Red', 'Sand']
    }],
    modifiers: [{
      name: 'Monogram embroidery',
      price: 4.5
    }, {
      name: 'Gift wrap',
      price: 2.0
    }]
  },
  prod3: {
    id: 'prod3',
    name: 'Swim Shorts',
    description: 'Quick-dry swim shorts for men, made from recycled polyester.',
    price: 34.99,
    salePrice: 29.99,
    sku: 'SS-003',
    stockQuantity: 28,
    category: 'Swimwear',
    imageUrl: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    isActive: true,
    options: [{
      name: 'Size',
      values: ['S', 'M', 'L', 'XL']
    }, {
      name: 'Color',
      values: ['Navy', 'Coral', 'Black']
    }],
    modifiers: [{
      name: 'Express delivery',
      price: 5.0
    }]
  }
};
export function ProductDetail() {
  const navigate = useNavigate();
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [params] = useSearchParams();
  const shopId = params.get('shop') || '';
  const product = productId ? mockProducts[productId] : null;
  if (!product) {
    return <div className="max-w-3xl mx-auto py-10 text-center" data-id="element-12409" data-mp="ebpkxyh65ve6">
        <p className="text-sm text-gray-500" data-id="element-12410" data-mp="9a06s9ynh1lv">Product not found.</p>
        <button onClick={() => navigate(-1)} className="mt-4 inline-flex items-center text-sm text-[#E2231A] hover:text-[#C41B14]" data-id="element-12411" data-mp="4c8gtlhkfpkb">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12412" data-mp="3vo9tmemxm1kj" />
          Go back
        </button>
      </div>;
  }
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-12413" data-mp="3mpogx0455gro">
      <div data-id="element-12414" data-mp="3mr9aonimsvoh">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-12415" data-mp="3mrizw8vuqyt4">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12416" data-mp="32nlhv82bxly3" />
          Back to shop
        </button>
        <div className="flex items-start justify-between" data-id="element-12417" data-mp="37mocrzolibsc">
          <div data-id="element-12418" data-mp="37l3hg7ugiotf">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12419" data-mp="37ordbdndjnbb">
              {product.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-12420" data-mp="3cjwxo814otxa">SKU {product.sku}</p>
          </div>
          <button onClick={() => navigate(`/product-edit/${product.id}?shop=${shopId}`)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm" data-id="element-12421" data-mp="3hironx991osh">
            <EditIcon size={14} data-id="element-12422" data-mp="2ttyrjxgw5awa" />
            Edit product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12423" data-mp="2ynb4vbg4dv9q">
        {/* Image */}
        <div className="lg:col-span-1" data-id="element-12424" data-mp="2162uusrbnizi">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12425" data-mp="265p8v4ez539d">
            <div className="aspect-square bg-gray-100" data-id="element-12426" data-mp="263cjw7astsmt">
              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" data-id="element-12427" data-mp="bq8o39n8o3t7" />
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12428" data-mp="lhczy0hmb8jt">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12429" data-mp="gnimheslvpt6">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12430" data-mp="uff7kyvuwzg2">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12431" data-mp="uht4zee0enqs">Overview</h2>
            </div>
            <div className="px-6 py-5 space-y-4" data-id="element-12432" data-mp="qgpnkngbcjng">
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-12433" data-mp="14d37s4c8ez5m">
                {product.description}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-12434" data-mp="zfumhuvsef05">
                <div data-id="element-12435" data-mp="366kcaz52fjw5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12436" data-mp="36612k5pkk1t7">
                    Price
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-12437" data-mp="318r7oajeuxxz">
                    MYR {product.price.toFixed(2)}
                  </p>
                </div>
                {product.salePrice != null && <div data-id="element-12438" data-mp="16yu7u62t6gm5">
                    <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12439" data-mp="16xj2w2xmn2tg">
                      Sale price
                    </p>
                    <p className="mt-1 text-base font-semibold text-red-600" data-id="element-12440" data-mp="1bvkhobo0sfz4">
                      MYR {product.salePrice.toFixed(2)}
                    </p>
                  </div>}
                <div data-id="element-12441" data-mp="12uw5znm3mrpe">
                  <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12442" data-mp="nzq153yqoyxn">
                    In stock
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-12443" data-mp="szugurrtppmt">
                    {product.stockQuantity}
                  </p>
                </div>
                <div data-id="element-12444" data-mp="2nctz6snl8xxs">
                  <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12445" data-mp="2nd2fyvmu0ne9">
                    Category
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-12446" data-mp="2lx83xe8p7uln">
                    {product.category}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-12447" data-mp="2byim1lgsya5e">
                {product.isActive ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-green-50 border border-green-200 text-green-700 text-xs font-medium" data-id="element-12448" data-mp="27wxdudf7qk1j">
                    <CheckCircleIcon size={12} data-id="element-12449" data-mp="2zn1rzucdqaub" /> Active
                  </span> : <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-gray-600 text-xs font-medium" data-id="element-12450" data-mp="2uw3boure5cr2">
                    <AlertCircleIcon size={12} data-id="element-12451" data-mp="2quhskro9k28f" /> Inactive
                  </span>}
              </div>
            </div>
          </div>

          {/* Options */}
          {product.options.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12452" data-mp="3ik2zpmxbip1y">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12453" data-mp="155clyv483ocg">
                <TagIcon size={16} className="text-gray-500" data-id="element-12454" data-mp="1a4gwpq6spu4w" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12455" data-mp="1e2wqqe1ao2rt">Options</h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-12456" data-mp="1j43y4bziyzxy">
                {product.options.map(opt => <div key={opt.name} data-id="element-12457" data-mp="1o12pwnvpuusz">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12458" data-mp="1o0b8cgyyexlp">
                      {opt.name}
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-id="element-12459" data-mp="1xt8j20xpfi9r">
                      {opt.values.map(v => <span key={v} className="inline-flex items-center px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-700" data-id="element-12460" data-mp="21t0vwpu4sakx">
                          {v}
                        </span>)}
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Modifiers */}
          {product.modifiers.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12461" data-mp="1voppad5kq0yg">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12462" data-mp="1ro5q741fwn9o">
                <PackageIcon size={16} className="text-gray-500" data-id="element-12463" data-mp="13yux88qbzx63" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12464" data-mp="yzicbtj0t065">
                  Modifiers
                </h2>
              </div>
              <div className="divide-y divide-gray-100" data-id="element-12465" data-mp="1cr6k5km8l2hb">
                {product.modifiers.map(m => <div key={m.name} className="px-6 py-3 flex items-center justify-between" data-id="element-12466" data-mp="17y3nh3m5xcww">
                    <p className="text-sm text-gray-700" data-id="element-12467" data-mp="g90l5tdu6cmw">{m.name}</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-12468" data-mp="g6o5xnzgqgdj">
                      +MYR {m.price.toFixed(2)}
                    </p>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}